/** hanzi-writer-data coordinates are Y-up; SVG coordinates are Y-down. */
export const HANZI_STROKE_TRANSFORM = 'translate(0 900) scale(1 -1)';
export const HANZI_STROKE_VIEW_BOX = '0 0 1024 1024';
export type StrokeGeometry = {transform?: string; viewBox?: string; glyph?: string};
type Matrix = [number, number, number, number, number, number];
type GeometryInput = StrokeGeometry | string | null | undefined;
export function strokeTransform(manifest?: GeometryInput): string {
  return (typeof manifest === 'string' ? manifest : manifest?.transform)?.trim() || HANZI_STROKE_TRANSFORM;
}
export function strokeViewBox(manifest?: StrokeGeometry | null): string {
  const value = manifest?.viewBox?.trim() || HANZI_STROKE_VIEW_BOX;
  const numbers = value.split(/[\s,]+/).map(Number);
  if (numbers.length !== 4 || !numbers.every(Number.isFinite) || numbers[2] <= 0 || numbers[3] <= 0) throw Error('Invalid stroke viewBox');
  return value;
}
const multiply = (a: Matrix, b: Matrix): Matrix => [a[0]*b[0]+a[2]*b[1], a[1]*b[0]+a[3]*b[1], a[0]*b[2]+a[2]*b[3], a[1]*b[2]+a[3]*b[3], a[0]*b[4]+a[2]*b[5]+a[4], a[1]*b[4]+a[3]*b[5]+a[5]];
/** Same composition order as an SVG transform list; also used by median labels. */
export function strokeMatrix(manifest?: GeometryInput): Matrix {
  const source = strokeTransform(manifest);
  let matrix: Matrix = [1,0,0,1,0,0], end = 0;
  for (const match of source.matchAll(/([a-zA-Z]+)\s*\(([^)]*)\)/g)) {
    if (source.slice(end,match.index).replace(/[\s,]/g,'')) throw Error('Invalid stroke transform');
    end = match.index! + match[0].length;
    const values = match[2].match(/[+-]?(?:\d*\.\d+|\d+\.?\d*)(?:e[+-]?\d+)?/gi) ?? [];
    if (match[2].replace(/[+-]?(?:\d*\.\d+|\d+\.?\d*)(?:e[+-]?\d+)?/gi,'').replace(/[\s,]/g,'')) throw Error('Invalid stroke transform');
    const v = values.map(Number); let next: Matrix;
    if (!v.every(Number.isFinite)) throw Error('Invalid stroke transform');
    if (match[1] === 'matrix' && v.length === 6) next = v as Matrix;
    else if (match[1] === 'translate' && [1,2].includes(v.length)) next = [1,0,0,1,v[0],v[1]??0];
    else if (match[1] === 'scale' && [1,2].includes(v.length)) next = [v[0],0,0,v[1]??v[0],0,0];
    else if (match[1] === 'rotate' && [1,3].includes(v.length)) {
      const a=v[0]*Math.PI/180, c=Math.cos(a),s=Math.sin(a),x=v[1]??0,y=v[2]??0;
      next=[c,s,-s,c,x-c*x+s*y,y-s*x-c*y];
    } else if (['skewX','skewY'].includes(match[1]) && v.length===1) {
      const t=Math.tan(v[0]*Math.PI/180);next=match[1]==='skewX'?[1,0,t,1,0,0]:[1,t,0,1,0,0];
    } else throw Error('Invalid stroke transform');
    matrix=multiply(matrix,next);
  }
  if (!end || source.slice(end).replace(/[\s,]/g,'')) throw Error('Invalid stroke transform');
  return matrix;
}
export function projectStrokeMedian(median: number[][], manifest?: GeometryInput): number[][] {
  const [a,b,c,d,e,f]=strokeMatrix(manifest);
  return median.map(([x,y])=>[a*x+c*y+e,b*x+d*y+f]);
}
/** A new in-memory value: never modifies an immutable stored manifest. */
export function normalizeStrokeManifest<T extends StrokeGeometry>(manifest: T): T & {transform:string;viewBox:string} {
  const transform=strokeTransform(manifest),viewBox=strokeViewBox(manifest);strokeMatrix(manifest);
  return manifest.transform===transform && manifest.viewBox===viewBox ? manifest as T & {transform:string;viewBox:string} : {...manifest,transform,viewBox};
}
export function strokeSvgAttributes(manifest?: StrokeGeometry) {
  return {viewBox:strokeViewBox(manifest),preserveAspectRatio:'xMidYMid meet',
    'data-stroke-transform':strokeTransform(manifest),'data-stroke-glyph':manifest?.glyph};
}
/** getBBox on the outer SVG includes the group's transform. Keep padding square. */
export function fitStrokeViewBox(svg: SVGSVGElement, fraction=.85) {
  const b=svg.getBBox(),size=Math.max(b.width,b.height)/fraction;
  if (Number.isFinite(size) && size>0) svg.setAttribute('viewBox',`${b.x+b.width/2-size/2} ${b.y+b.height/2-size/2} ${size} ${size}`);
}
